import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ASK_AI_SERVICE,
  AskAiResult,
  ClipboardService,
  Utils,
} from '@services';
import { SpinnerComponent } from '@ui-lib';

export interface AskAiResultDialogData {
  headerTitle: string;
  question: string;
  result: AskAiResult;
}

const DEFAULT_FONT_SIZE_INDEX = 2;

const FONT_SIZES = [
  'text-xs',
  'text-sm',
  'text-base',
  'text-lg',
  'text-xl',
  'text-2xl',
  'text-3xl',
  'text-4xl',
] as const;

@Component({
  selector: 'provider-portal-ask-ai-result-dialog',
  imports: [MatIconModule, SpinnerComponent],
  templateUrl: './ask-ai-result-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AskAiResultDialogComponent {
  private readonly dialogRef = inject(DialogRef);
  private readonly clipboard = inject(ClipboardService);
  private readonly askAiService = inject(ASK_AI_SERVICE);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  readonly data: AskAiResultDialogData = inject(DIALOG_DATA);

  readonly fontSizeIndex = signal(DEFAULT_FONT_SIZE_INDEX);
  readonly copied = signal(false);
  readonly savingPdf = signal(false);
  readonly savedPdf = signal(false);

  readonly fontSizeClass = computed(() => FONT_SIZES[this.fontSizeIndex()]);
  readonly fontSizeMaxIndex = FONT_SIZES.length - 1;

  readonly displayedHtml = computed<SafeHtml>(() => {
    const text = this.data.result.answerText;
    return text ? this.sanitizer.bypassSecurityTrustHtml(text) : '';
  });

  readonly generatedDate = new Date(
    this.data.result.createdAtUtc
  ).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  close(): void {
    this.dialogRef.close();
  }

  decreaseFontSize(): void {
    if (this.fontSizeIndex() > 0) this.fontSizeIndex.update(i => i - 1);
  }

  increaseFontSize(): void {
    if (this.fontSizeIndex() < this.fontSizeMaxIndex)
      this.fontSizeIndex.update(i => i + 1);
  }

  copy(): void {
    const text = this.data.result.answerText;
    if (!text) return;
    this.clipboard.copyHtml(text).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    });
  }

  downloadPdf(): void {
    if (this.savingPdf()) return;
    const { id } = this.data.result;
    this.savingPdf.set(true);
    document.body.classList.add('cursor-wait');
    this.askAiService
      .getPdf(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: blob => {
          Utils.downloadBlob(blob, `AI_Answer_${id}.pdf`);
          this.savingPdf.set(false);
          this.savedPdf.set(true);
          document.body.classList.remove('cursor-wait');
          setTimeout(() => this.savedPdf.set(false), 3000);
        },
        error: () => {
          this.savingPdf.set(false);
          document.body.classList.remove('cursor-wait');
        },
      });
  }
}
